import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Trophy, AlertTriangle, Rocket, TrendingUp, TrendingDown, Minus, Phone } from "lucide-react";
import { Skel, TrendBadge } from "@/pages/dashboard/DashboardParts";

export function RankingDialog({ open, onOpenChange, marksDetail, leaderboard, detailLoading, cn, sec }) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ranking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" /> Class ranking
          </DialogTitle>
          <DialogDescription>
            {marksDetail?.exam_label || leaderboard?.exam_label || "Latest exam"} · Class {cn} Section {sec}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {detailLoading || !marksDetail ? (
            <div className="space-y-2">{[0,1,2,3].map(i => <Skel key={i} />)}</div>
          ) : (marksDetail.ranking || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No exam results published yet.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {marksDetail.ranking.map((r) => (
                <div key={r.student_id} className="flex items-center gap-3 py-2.5" data-testid={`rank-row-${r.rank}`}>
                  <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${r.rank===1?"bg-amber-100 text-amber-700":r.rank===2?"bg-slate-200 text-slate-600":r.rank===3?"bg-orange-100 text-orange-700":"bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"}`}>{r.rank}</span>
                  <button type="button" onClick={() => { onOpenChange(false); navigate(`/search?q=${encodeURIComponent(r.roll_number || r.name)}`); }} className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate hover:text-sky-600">{r.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono">#{r.roll_number}</div>
                  </button>
                  <TrendBadge trend={r.trend} prev={r.prev_percentage} />
                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-300 w-14 text-right">{r.percentage != null ? `${r.percentage}%` : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImprovementDialog({
  open, onOpenChange, marksDetail, leaderboard, detailLoading, canCallParents,
  improvers, droppers, setBatchTarget, setCallTarget,
}) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="improvement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-emerald-500" /> Progress vs {marksDetail?.prev_exam_label || "previous exam"}
          </DialogTitle>
          <DialogDescription>
            Students ranked by change in {marksDetail?.exam_label || leaderboard?.exam_label || "the latest exam"}. Call parents to celebrate progress or flag a decline.
          </DialogDescription>
        </DialogHeader>
        {canCallParents && (improvers.length > 0 || droppers.length > 0) && (
          <div className="flex flex-wrap gap-2" data-testid="improvement-batch-actions">
            {improvers.length > 0 && (
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setBatchTarget({ mode: "congrats", students: improvers })} data-testid="call-all-improvers-btn">
                <Phone className="h-3.5 w-3.5 mr-1.5" /> Call all improvers ({improvers.length})
              </Button>
            )}
            {droppers.length > 0 && (
              <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setBatchTarget({ mode: "concern", students: droppers })} data-testid="call-all-dropped-btn">
                <Phone className="h-3.5 w-3.5 mr-1.5" /> Call all dropped ({droppers.length})
              </Button>
            )}
          </div>
        )}
        <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2">
          {detailLoading || !marksDetail ? (
            <div className="space-y-2">{[0,1,2,3].map(i => <Skel key={i} />)}</div>
          ) : (marksDetail.improvement || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Progress appears once a second exam is published.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {marksDetail.improvement.map((s) => (
                <div key={s.student_id} className="flex items-center gap-3 py-2.5" data-testid={`improve-row-${s.roll_number}`}>
                  <button type="button" onClick={() => { onOpenChange(false); navigate(`/search?q=${encodeURIComponent(s.roll_number || s.name)}`); }} className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate hover:text-sky-600">{s.name}</div>
                    <div className="text-[11px] text-slate-400">{s.prev_percentage}% → <span className="text-slate-600 dark:text-slate-300 font-medium">{s.percentage}%</span></div>
                  </button>
                  <span className={`inline-flex items-center gap-0.5 text-xs font-semibold w-14 justify-end ${s.jump > 0 ? "text-emerald-600 dark:text-emerald-300" : s.jump < 0 ? "text-rose-500" : "text-slate-400"}`}>
                    {s.jump > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : s.jump < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                    {s.jump > 0 ? "+" : ""}{s.jump}%
                  </span>
                  {canCallParents && s.jump > 0 ? (
                    <Button size="sm" variant="outline" className="h-8 px-2.5 border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => { onOpenChange(false); setCallTarget({ ...s, mode: "congrats" }); }} data-testid={`improve-call-${s.roll_number}`} title="Congratulate parent">
                      <Phone className="h-3.5 w-3.5" />
                    </Button>
                  ) : canCallParents && s.jump < 0 ? (
                    <Button size="sm" variant="outline" className="h-8 px-2.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => { onOpenChange(false); setCallTarget({ ...s, mode: "concern" }); }} data-testid={`drop-call-${s.roll_number}`} title="Warn parent about decline">
                      <Phone className="h-3.5 w-3.5" />
                    </Button>
                  ) : <span className="w-[42px]" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SingleCallDialog({ callTarget, setCallTarget, callSending, sendSingleCall, marksDetail, leaderboard }) {
  const concern = callTarget?.mode === "concern";
  return (
    <Dialog open={!!callTarget} onOpenChange={(o) => { if (!o) setCallTarget(null); }}>
      <DialogContent data-testid="single-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {concern ? <AlertTriangle className="h-5 w-5 text-rose-500" /> : <Rocket className="h-5 w-5 text-emerald-500" />}
            {concern ? "Notify" : "Congratulate"} {callTarget?.name}&apos;s parent
          </DialogTitle>
          <DialogDescription>
            Place an automated voice call about {callTarget?.name}&apos;s{" "}
            {concern ? (
              <><span className="font-semibold text-rose-600">{callTarget?.jump}%</span> decline (now {callTarget?.percentage}%)</>
            ) : (
              <><span className="font-semibold text-emerald-600">+{callTarget?.jump}%</span> improvement (now {callTarget?.percentage}%)</>
            )}{" "}
            in {marksDetail?.exam_label || leaderboard?.exam_label || "the latest exam"}.
          </DialogDescription>
        </DialogHeader>
        <div className={`rounded-lg p-3 text-sm ${concern ? "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300" : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"}`}>
          {concern
            ? "The parent will be gently asked to support and encourage their child to get back on track. Review it later under Calls."
            : "The parent will receive a short, friendly call encouraging the student to keep up the effort. Review it later under Calls."}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCallTarget(null)} disabled={callSending} data-testid="single-call-cancel-btn">Cancel</Button>
          <Button onClick={sendSingleCall} disabled={callSending} className={concern ? "bg-rose-500 hover:bg-rose-600" : "bg-emerald-500 hover:bg-emerald-600"} data-testid="single-call-confirm-btn">
            <Phone className="h-4 w-4 mr-1.5" /> {callSending ? "Calling…" : "Call parent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BatchCallDialog({ batchTarget, setBatchTarget, callSending, sendBatchCalls, marksDetail }) {
  const concern = batchTarget?.mode === "concern";
  const n = batchTarget?.students?.length || 0;
  return (
    <Dialog open={!!batchTarget} onOpenChange={(o) => { if (!o) setBatchTarget(null); }}>
      <DialogContent data-testid="batch-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {concern ? <AlertTriangle className="h-5 w-5 text-rose-500" /> : <Rocket className="h-5 w-5 text-emerald-500" />}
            Call {n} parent{n > 1 ? "s" : ""} — {concern ? "decline notice" : "congratulations"}
          </DialogTitle>
          <DialogDescription>
            {concern
              ? `An automated call will go to the parents of ${n} student${n > 1 ? "s" : ""} whose marks dropped in ${marksDetail?.exam_label || "the latest exam"}, requesting their support.`
              : `An automated congratulatory call will go to the parents of ${n} student${n > 1 ? "s" : ""} who improved in ${marksDetail?.exam_label || "the latest exam"}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm text-slate-600 dark:text-slate-300">
          {(batchTarget?.students || []).map((s) => (
            <div key={s.student_id} className="flex justify-between py-0.5">
              <span className="truncate">{s.name}</span>
              <span className={`font-semibold ${concern ? "text-rose-500" : "text-emerald-600"}`}>{s.jump > 0 ? "+" : ""}{s.jump}%</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">Calls are placed one per second to respect rate limits.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setBatchTarget(null)} disabled={callSending} data-testid="batch-call-cancel-btn">Cancel</Button>
          <Button onClick={sendBatchCalls} disabled={callSending} className={concern ? "bg-rose-500 hover:bg-rose-600" : "bg-emerald-500 hover:bg-emerald-600"} data-testid="batch-call-confirm-btn">
            <Phone className="h-4 w-4 mr-1.5" /> {callSending ? "Queuing…" : `Call ${n} parent${n > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
